import { Component, type ReactNode } from 'react';
import { AppAlert } from '@/components/AppAlert';
import { Button } from '@/components/ui/button';
import { reloadApp } from '@/lib/app-update';
import { isChunkLoadError } from '@/lib/client-errors';
import { cn } from '@/lib/utils';

/**
 * - `root`: the whole app, portal or extension panel (in its main.tsx); nothing else is left to show.
 * - `page`: one page, inside the app shell (PageLayout), so the sidebar and top bar stay.
 * - `section`: one part of a page that renders on its own: a detail pane, a heavy dialog, a side panel.
 * - `chart`: one chart; the rest of the page carries on.
 */
export type ErrorBoundaryVariant = 'root' | 'page' | 'section' | 'chart';

interface ErrorBoundaryProps {
  children: ReactNode;
  variant?: ErrorBoundaryVariant;
  /** When any of these change, the boundary tries again (the id of the record shown, the path). */
  resetKeys?: readonly unknown[];
  /** Section: what couldn't be shown, "Couldn't show this conversation". */
  title?: string;
  /** Chart: the chart's height, so the page doesn't jump. */
  height?: number;
  /** Root: where "Go to Home" goes; null for none (the extension). */
  homeHref?: string | null;
  /** Reload for root and page; the extension passes its own. */
  onReload?: () => void;
  className?: string;
}

function keysChanged(a: readonly unknown[] = [], b: readonly unknown[] = []): boolean {
  return a.length !== b.length || a.some((value, i) => !Object.is(value, b[i]));
}

/**
 * Keeps a crash to the part of the screen that crashed, and says which kind it was: a page file
 * that couldn't load (usually a deploy since the tab opened) asks for a reload; a bug offers
 * Try again. The React root reports every caught error (lib/client-errors.ts), so this doesn't.
 */
interface ErrorBoundaryState {
  /** Separate from `error`: a thrown 0, false, '' or null is still a crash. */
  failed: boolean;
  error: unknown;
}

export class ErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
  override state: ErrorBoundaryState = { failed: false, error: null };

  static getDerivedStateFromError(error: unknown): ErrorBoundaryState {
    return { failed: true, error };
  }

  override componentDidUpdate(prev: ErrorBoundaryProps) {
    if (this.state.failed && keysChanged(prev.resetKeys, this.props.resetKeys)) {
      this.setState({ failed: false, error: null });
    }
  }

  private reset = () => this.setState({ failed: false, error: null });

  override render() {
    const { failed, error } = this.state;
    if (!failed) return this.props.children;
    const { variant = 'page', title, height, homeHref, onReload, className } = this.props;
    return (
      <ErrorFallback
        variant={variant}
        chunk={isChunkLoadError(error)}
        title={title}
        height={height}
        homeHref={homeHref}
        onReload={onReload}
        onRetry={this.reset}
        className={className}
      />
    );
  }
}

interface ErrorFallbackProps {
  variant: ErrorBoundaryVariant;
  /** A code file that couldn't load, rather than a bug. */
  chunk?: boolean;
  title?: string;
  height?: number;
  homeHref?: string | null;
  onReload?: () => void;
  onRetry?: () => void;
  className?: string;
}

/** What a boundary shows in place of what crashed. Exported for the /dev/ui gallery. */
export function ErrorFallback({
  variant,
  chunk = false,
  title,
  height,
  homeHref = '/',
  onReload = reloadApp,
  onRetry,
  className,
}: ErrorFallbackProps) {
  if (variant === 'chart') {
    return (
      <div
        role="status"
        className={cn(
          'flex flex-col items-center justify-center gap-2 rounded-md border border-dashed text-sm text-muted-foreground',
          className
        )}
        style={{ minHeight: height ?? 200 }}
      >
        Chart couldn&apos;t load
        {onRetry && !chunk ? (
          <Button type="button" variant="ghost" size="xs" onClick={onRetry}>
            Try again
          </Button>
        ) : null}
      </div>
    );
  }

  if (variant === 'section') {
    return (
      <AppAlert
        tone="critical"
        compact
        className={className}
        title={title ?? "Couldn't show this part of the page"}
        action={
          chunk ? (
            <Button type="button" variant="outline" size="xs" onClick={onReload}>
              Reload
            </Button>
          ) : onRetry ? (
            <Button type="button" variant="outline" size="xs" onClick={onRetry}>
              Try again
            </Button>
          ) : undefined
        }
      >
        <p>
          {chunk
            ? 'Part of jflo didn’t download. Reload to get the latest version.'
            : 'jflo hit a problem it didn’t expect, and it has been reported.'}
        </p>
      </AppAlert>
    );
  }

  const heading =
    variant === 'root'
      ? chunk
        ? 'jflo couldn’t finish loading'
        : 'jflo couldn’t show this screen'
      : chunk
        ? 'This page couldn’t load'
        : 'This page couldn’t be shown';
  const body = chunk
    ? 'jflo may have been updated since this tab opened, or the connection dropped. Reload to get the latest version.'
    : variant === 'root'
      ? 'jflo hit a problem it didn’t expect, and it has been reported. Reload to carry on.'
      : 'jflo hit a problem it didn’t expect, and it has been reported. Try again, or reload the page.';

  return (
    <div
      role="alert"
      className={cn(
        'mx-auto flex max-w-md flex-col items-center gap-3 px-4 text-center',
        variant === 'root' ? 'min-h-dvh justify-center' : 'py-16',
        className
      )}
    >
      <p className="text-base font-medium">{heading}</p>
      <p className="text-sm text-muted-foreground">{body}</p>
      <div className="flex flex-wrap justify-center gap-2">
        {variant === 'page' && !chunk && onRetry ? (
          <Button type="button" variant="outline" onClick={onRetry}>
            Try again
          </Button>
        ) : null}
        <Button type="button" onClick={onReload}>
          Reload
        </Button>
        {variant === 'root' && homeHref ? (
          <Button asChild variant="outline">
            <a href={homeHref}>Go to Home</a>
          </Button>
        ) : null}
      </div>
    </div>
  );
}
